import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { decodeViewState, encodeViewState, resetViewState as reset } from '../../../../shared/riskregister/viewState';
import type { Payload, ViewState } from '../../../../shared/riskregister/types';
import { TAB_PARAM } from '../tabs';

const PRESERVED_PARAMS = ['asOf', 'chgWindow', TAB_PARAM];

function has(change: Partial<ViewState>, key: keyof ViewState): boolean {
  return Object.prototype.hasOwnProperty.call(change, key);
}

export function enforceViewStateRules(
  current: ViewState,
  change: Partial<ViewState>,
  payload: Payload,
): ViewState {
  const next = { ...current, ...change };

  if (has(change, 'lane') && change.lane) {
    return { ...reset(payload), lane: change.lane };
  }
  if (!has(change, 'lane') && current.lane) next.lane = '';
  if (has(change, 'cat') && next.cat !== 'any') next.catsrc = 'any';
  if (has(change, 'catsrc') && next.catsrc !== 'any') next.cat = 'any';
  if (has(change, 'acct')) next.unit = '';

  return next;
}

export function useViewState(payload: Payload | undefined) {
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = React.useState<ViewState | null>(() => (
    payload ? decodeViewState(location.search, payload) : null
  ));

  React.useEffect(() => {
    if (payload) setState(decodeViewState(location.search, payload));
  }, [location.search, payload]);

  const writeState = React.useCallback((next: ViewState) => {
    if (!payload) return;
    const params = new URLSearchParams(encodeViewState(next, payload));
    const current = new URLSearchParams(location.search);
    // Params owned outside the view state (the as-of date, the change window, and the active tab)
    // are not produced by encodeViewState, so they have to be carried across or a filter change
    // would drop them and send the page back to its defaults.
    PRESERVED_PARAMS.forEach((name) => {
      const value = current.get(name);
      if (value) params.set(name, value);
    });
    const search = params.toString();
    setState(next);
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [history, location.pathname, location.search, payload]);

  const setViewState = React.useCallback((change: Partial<ViewState>) => {
    if (!payload || !state) return;
    writeState(enforceViewStateRules(state, change, payload));
  }, [payload, state, writeState]);

  const resetViewState = React.useCallback(() => {
    if (payload) writeState(reset(payload));
  }, [payload, writeState]);

  return { state, setViewState, resetViewState };
}
